import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Divider,
  Typography,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Switch,
} from '@mui/material';
import { Quiz, CreateQuizData, UpdateQuizData, MissedEventPolicy } from '../../types/quiz';

// Mirrors the database defaults (see V49 and V68) so the form shows the same
// values the server would apply if these fields were omitted.
const DEFAULT_MAX_SPEED_BONUS = 30;
const DEFAULT_QUESTION_TIME_LIMIT = 30;
const DEFAULT_MAX_ATTEMPTS = 1;
// Matches V69's `lobby_opens_minutes` column default.
const DEFAULT_LOBBY_OPENS_MINUTES = 5;
const DEFAULT_MISSED_EVENT_POLICY: MissedEventPolicy = 'replay_unranked';

/**
 * A `datetime-local` input works in the browser's local wall-clock time with
 * no timezone in the string at all, so `new Date(value)` correctly treats it
 * as local and `.toISOString()` converts to the UTC `Z` string the rest of
 * the stack (including `parseDateSafe`) assumes.
 */
function isoToDatetimeLocalValue(iso: string): string {
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

interface QuizFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateQuizData | UpdateQuizData) => Promise<void>;
  initialData?: Quiz;
}

const QuizFormDialog: React.FC<QuizFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [quizMode, setQuizMode] = useState<'async' | 'live'>('async');
  const [startsAtLocal, setStartsAtLocal] = useState('');
  const [lobbyOpensMinutes, setLobbyOpensMinutes] = useState(DEFAULT_LOBBY_OPENS_MINUTES);
  const [missedEventPolicy, setMissedEventPolicy] = useState<MissedEventPolicy>(DEFAULT_MISSED_EVENT_POLICY);
  const [maxSpeedBonus, setMaxSpeedBonus] = useState(DEFAULT_MAX_SPEED_BONUS);
  const [defaultQuestionTimeLimit, setDefaultQuestionTimeLimit] = useState(DEFAULT_QUESTION_TIME_LIMIT);
  const [maxAttempts, setMaxAttempts] = useState(DEFAULT_MAX_ATTEMPTS);
  const [unlimitedAttempts, setUnlimitedAttempts] = useState(false);
  const [randomizeQuestions, setRandomizeQuestions] = useState(false);
  const [randomizeOptions, setRandomizeOptions] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title);
      setDescription(initialData.description || '');
      setQuizMode(initialData.quizMode ?? 'async');
      setStartsAtLocal(initialData.startsAt ? isoToDatetimeLocalValue(initialData.startsAt) : '');
      setLobbyOpensMinutes(initialData.lobbyOpensMinutes ?? DEFAULT_LOBBY_OPENS_MINUTES);
      setMissedEventPolicy(initialData.missedEventPolicy ?? DEFAULT_MISSED_EVENT_POLICY);
      setMaxSpeedBonus(initialData.maxSpeedBonus ?? DEFAULT_MAX_SPEED_BONUS);
      setDefaultQuestionTimeLimit(initialData.defaultQuestionTimeLimit ?? DEFAULT_QUESTION_TIME_LIMIT);
      setUnlimitedAttempts(initialData.maxAttempts === null);
      setMaxAttempts(initialData.maxAttempts ?? DEFAULT_MAX_ATTEMPTS);
      setRandomizeQuestions(initialData.randomizeQuestions ?? false);
      setRandomizeOptions(initialData.randomizeOptions ?? false);
    } else {
      setTitle('');
      setDescription('');
      setQuizMode('async');
      setStartsAtLocal('');
      setLobbyOpensMinutes(DEFAULT_LOBBY_OPENS_MINUTES);
      setMissedEventPolicy(DEFAULT_MISSED_EVENT_POLICY);
      setMaxSpeedBonus(DEFAULT_MAX_SPEED_BONUS);
      setDefaultQuestionTimeLimit(DEFAULT_QUESTION_TIME_LIMIT);
      setMaxAttempts(DEFAULT_MAX_ATTEMPTS);
      setUnlimitedAttempts(false);
      setRandomizeQuestions(false);
      setRandomizeOptions(false);
    }
    setErrors({});
  }, [initialData, open]);

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    } else if (title.trim().length < 3) {
      newErrors.title = 'Title must be at least 3 characters';
    } else if (title.trim().length > 200) {
      newErrors.title = 'Title must be less than 200 characters';
    }

    if (description && description.length > 2000) {
      newErrors.description = 'Description must be less than 2000 characters';
    }

    if (!Number.isInteger(maxSpeedBonus) || maxSpeedBonus < 0 || maxSpeedBonus > 100) {
      newErrors.maxSpeedBonus = 'Speed bonus must be between 0 and 100';
    }

    if (!Number.isInteger(defaultQuestionTimeLimit) || defaultQuestionTimeLimit < 5 || defaultQuestionTimeLimit > 300) {
      newErrors.defaultQuestionTimeLimit = 'Time limit must be between 5 and 300 seconds';
    }

    if (!unlimitedAttempts && (!Number.isInteger(maxAttempts) || maxAttempts < 1)) {
      newErrors.maxAttempts = 'Attempts must be at least 1';
    }

    // Deliberately not validated here: whether a live quiz has a start time
    // at all, or whether it's in the future. That compound rule ("published
    // + live requires a future startsAt") is enforced server-side and
    // depends on publish state this dialog doesn't own — duplicating it here
    // risks drifting from the actual rule. The server's 400 message surfaces
    // through the existing QuizBuilderContext error handling instead.
    if (
      quizMode === 'live' &&
      (!Number.isInteger(lobbyOpensMinutes) || lobbyOpensMinutes < 0 || lobbyOpensMinutes > 60)
    ) {
      newErrors.lobbyOpensMinutes = 'Lobby must open between 0 and 60 minutes before the start';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setSubmitting(true);
      const data: CreateQuizData | UpdateQuizData = {
        title: title.trim(),
        description: description.trim() || undefined,
        quizMode,
        // Explicit null (rather than omitting the key) when switching back
        // to self-paced, so an existing schedule is actually cleared rather
        // than left stale on the server.
        startsAt: quizMode === 'live' && startsAtLocal ? new Date(startsAtLocal).toISOString() : null,
        ...(quizMode === 'live' ? { lobbyOpensMinutes, missedEventPolicy } : {}),
        maxSpeedBonus,
        defaultQuestionTimeLimit,
        maxAttempts: unlimitedAttempts ? null : maxAttempts,
        randomizeQuestions,
        randomizeOptions,
      };

      await onSubmit(data);
      onClose();
    } catch (error) {
      console.error('Error submitting quiz:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{initialData ? 'Edit Quiz' : 'Create New Quiz'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Quiz Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              error={!!errors.title}
              helperText={errors.title}
              required
              autoFocus
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              error={!!errors.description}
              helperText={errors.description}
              multiline
              rows={3}
              placeholder="Optional description of the quiz..."
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Format
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <FormControl>
              <FormLabel id="quiz-mode-label">Quiz mode</FormLabel>
              <RadioGroup
                row
                aria-labelledby="quiz-mode-label"
                value={quizMode}
                onChange={(e) => setQuizMode(e.target.value as 'async' | 'live')}
              >
                <FormControlLabel value="async" control={<Radio />} label="Self-paced" />
                <FormControlLabel value="live" control={<Radio />} label="Live event" />
              </RadioGroup>
            </FormControl>
          </Grid>

          {quizMode === 'live' && (
            <>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Start time"
                  InputLabelProps={{ shrink: true }}
                  value={startsAtLocal}
                  onChange={(e) => setStartsAtLocal(e.target.value)}
                  error={!!errors.startsAt}
                  helperText={errors.startsAt || 'Your local time — sent to the server in UTC.'}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Lobby opens (minutes before)"
                  value={lobbyOpensMinutes}
                  onChange={(e) => setLobbyOpensMinutes(parseInt(e.target.value, 10))}
                  error={!!errors.lobbyOpensMinutes}
                  helperText={errors.lobbyOpensMinutes || '0–60'}
                  inputProps={{ min: 0, max: 60 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  SelectProps={{ native: true }}
                  label="If a student misses it"
                  value={missedEventPolicy}
                  onChange={(e) => setMissedEventPolicy(e.target.value as MissedEventPolicy)}
                  helperText="What a student sees after the event ends without them"
                >
                  <option value="replay_unranked">Replay unranked</option>
                  <option value="locked_out">Locked out</option>
                  <option value="replay_ranked">Replay ranked</option>
                </TextField>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Scoring &amp; Behaviour
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Max Speed Bonus"
              value={maxSpeedBonus}
              onChange={(e) => setMaxSpeedBonus(parseInt(e.target.value, 10))}
              error={!!errors.maxSpeedBonus}
              helperText={
                errors.maxSpeedBonus ||
                'Extra points for the fastest correct answer, on top of the 10 base points. 0 disables the speed bonus.'
              }
              inputProps={{ min: 0, max: 100 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Default Question Time Limit (seconds)"
              value={defaultQuestionTimeLimit}
              onChange={(e) => setDefaultQuestionTimeLimit(parseInt(e.target.value, 10))}
              error={!!errors.defaultQuestionTimeLimit}
              helperText={
                errors.defaultQuestionTimeLimit ||
                'Used for questions that do not set their own limit (5-300)'
              }
              inputProps={{ min: 5, max: 300 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Max Attempts"
              value={unlimitedAttempts ? '' : maxAttempts}
              onChange={(e) => setMaxAttempts(parseInt(e.target.value, 10))}
              error={!!errors.maxAttempts}
              helperText={errors.maxAttempts || 'How many times a student may take this quiz'}
              disabled={unlimitedAttempts}
              inputProps={{ min: 1 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={unlimitedAttempts}
                  onChange={(e) => setUnlimitedAttempts(e.target.checked)}
                />
              }
              label="Unlimited attempts"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={randomizeQuestions}
                  onChange={(e) => setRandomizeQuestions(e.target.checked)}
                />
              }
              label="Randomize question order"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={randomizeOptions}
                  onChange={(e) => setRandomizeOptions(e.target.checked)}
                />
              }
              label="Randomize answer order"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={submitting}>
          {submitting ? 'Saving...' : initialData ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuizFormDialog;
